import {
  cardioLogSchema,
  cardioPrescriptionSchema,
  paceFrom,
  type CardioLog,
  type CardioPrescription,
} from '@cadencia/contracts';
import type { CardioSlot } from '@cadencia/domain';
import { addDays } from '../../lib/dates.js';
import { db } from '../db.js';
import { newSyncColumns, putNewRows, putRow, type LocalTable, type WriteContext } from '../writes.js';

/**
 * Cardio: prescripcion y registro (docs/DATA_MODEL.md §11).
 *
 * Dos tablas y no una, por el mismo motivo que `set_prescriptions` y
 * `performed_sets`: si compartieran fila, registrar una carrera reescribiria lo
 * que se habia planificado y el sistema perderia la capacidad de saber cuanto
 * se cumple de lo que propone.
 */

export async function listCardioOfWeek(
  userId: string,
  weekStart: string,
): Promise<CardioPrescription[]> {
  const filas = await db.cardioPrescriptions.where('userId').equals(userId).toArray();
  return filas
    .filter(
      (fila) =>
        fila.deletedAt === null &&
        fila.scheduledDate >= weekStart &&
        fila.scheduledDate < addDays(weekStart, 7),
    )
    .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
}

export async function getCardioOfDay(
  userId: string,
  date: string,
): Promise<CardioPrescription | undefined> {
  const filas = await db.cardioPrescriptions.where('scheduledDate').equals(date).toArray();
  return filas.find((fila) => fila.userId === userId && fila.deletedAt === null);
}

/**
 * Guarda las prescripciones de una semana, sin duplicar las que ya existan.
 *
 * La comprobacion es por DIA y no por identificador: dos carreras el mismo dia
 * no es lo que el motor quiere decir cuando propone tres a la semana, y esta
 * funcion se llama cada vez que se materializa la semana.
 */
export async function saveCardioPrescriptions(
  ctx: WriteContext,
  slots: readonly CardioSlot[],
): Promise<CardioPrescription[]> {
  if (slots.length === 0) return [];

  const existentes = new Set(
    (await db.cardioPrescriptions.where('userId').equals(ctx.userId).toArray())
      .filter((fila) => fila.deletedAt === null)
      .map((fila) => fila.scheduledDate),
  );

  const nuevas = slots
    .filter((slot) => !existentes.has(slot.date))
    .map((slot) =>
      cardioPrescriptionSchema.parse({
        ...newSyncColumns(ctx),
        sessionId: null,
        scheduledDate: slot.date,
        modality: slot.modality,
        targetMinutes: slot.minutes,
        targetDistanceKm: null,
        targetIntensity: slot.intensity,
        rationale: slot.rationale,
      }),
    );

  if (nuevas.length === 0) return [];

  await putNewRows(
    ctx,
    nuevas.map((row) => ({ table: 'cardioPrescriptions' as LocalTable, row })),
  );
  return nuevas;
}

export interface CardioLogInput {
  prescriptionId: string | null;
  performedOn: string;
  durationSeconds: number;
  distanceKm: number | null;
  perceivedEffort: number | null;
  notes: string | null;
}

/**
 * Registra una carrera.
 *
 * `prescriptionId` puede ser `null`: el §11 permite de forma explicita anotar
 * cardio no prescrito. Salir a correr sin que lo mande la aplicacion tiene que
 * poder registrarse.
 *
 * El ritmo se CALCULA aqui y no se pide: es un dato derivado de duracion y
 * distancia, y dejar que se escriba a mano abriria la puerta a que los tres
 * numeros se contradigan.
 */
export async function logCardio(
  ctx: WriteContext,
  input: CardioLogInput,
): Promise<CardioLog> {
  const row = cardioLogSchema.parse({
    ...newSyncColumns(ctx),
    ...input,
    paceSecondsPerKm: paceFrom(input.durationSeconds, input.distanceKm),
  });

  await putNewRows(ctx, [{ table: 'cardioLogs', row }]);
  return row;
}

export async function listRecentCardioLogs(userId: string, limite = 20): Promise<CardioLog[]> {
  const filas = await db.cardioLogs.where('userId').equals(userId).toArray();
  return filas
    .filter((fila) => fila.deletedAt === null)
    .sort((a, b) => b.performedOn.localeCompare(a.performedOn))
    .slice(0, limite);
}

/** Carreras ya registradas de una tanda de prescripciones. */
export async function logsByPrescription(
  userId: string,
  prescriptionIds: readonly string[],
): Promise<Map<string, CardioLog>> {
  if (prescriptionIds.length === 0) return new Map();
  const buscados = new Set(prescriptionIds);
  const filas = await db.cardioLogs.where('userId').equals(userId).toArray();

  const salida = new Map<string, CardioLog>();
  for (const fila of filas) {
    if (fila.deletedAt !== null) continue;
    if (fila.prescriptionId === null || !buscados.has(fila.prescriptionId)) continue;
    salida.set(fila.prescriptionId, fila);
  }
  return salida;
}

/**
 * Marca una prescripcion como borrada.
 *
 * Borrado suave, como todo lo sincronizable: un `delete` de verdad no dejaria
 * rastro que enviar y el otro dispositivo seguiria viendo la carrera.
 */
export async function cancelCardioPrescription(
  ctx: WriteContext,
  prescription: CardioPrescription,
  now: string,
): Promise<void> {
  await putRow(ctx, 'cardioPrescriptions', { ...prescription, deletedAt: now });
}
